import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Music2, Volume2, Monitor, Video, Palette, Zap, ArrowUpRight, UtensilsCrossed, Sofa, ShieldCheck, Droplets } from 'lucide-react';

const services = [
  {
    icon: Music2,
    title: 'Entertainment Bookings',
    description:
      'Bands, MCs, solo artists, promotional models, and dancers to bring the wow factor to your event.',
    image: '/brand/service-entertainment.webp',
    link: '/services#entertain',
  },
  {
    icon: Volume2,
    title: 'Technical Production',
    description:
      'Professional sound systems, intelligent lighting design, and robust staging solutions for flawless execution.',
    image: '/brand/approach.webp',
    link: '/services#technical',
  },
  {
    icon: Monitor,
    title: 'LED Screens',
    description:
      'High-resolution LED screen setups for vivid visuals, live feeds, branding, and immersive audience experiences.',
    image: '/brand/service-led.webp',
    link: '/services#led',
  },
  {
    icon: Video,
    title: 'Live Streaming & Recording',
    description:
      'Professional multi-camera live streaming and event recording for audiences beyond the venue.',
    image: '/brand/service-streaming.webp',
    link: '/services#streaming',
  },
  {
    icon: Palette,
    title: 'Custom Content Creation',
    description:
      'Bespoke voice-overs, promotional videos, motion graphics, and print design to elevate your event brand.',
    image: '/brand/service-content.webp',
    link: '/services#content',
  },
  {
    icon: Zap,
    title: 'Special Effects',
    description:
      'Gobo projections, laser shows, intelligent lighting, smoke machines, and low fog for jaw-dropping atmospherics.',
    image: '/brand/service-technical.webp',
    link: '/services#effects',
  },
  {
    icon: UtensilsCrossed,
    title: 'Catering and Bar',
    description:
      'Curated menus and staffed bar services, coordinated end-to-end with trusted suppliers to match the scale and tone of your event.',
    image: '/brand/service-catering.webp',
    link: '/services#catering',
  },
  {
    icon: Sofa,
    title: 'Décor and Furniture',
    description:
      'Sourced, designed, and installed to reflect your brand and theme — every space considered before a guest arrives.',
    image: '/brand/service-decor.webp',
    link: '/services#decor',
  },
  {
    icon: ShieldCheck,
    title: 'Health & Safety / JOC',
    description:
      'Full health and safety planning and Joint Operations Centre coordination — so your event is compliant and every contingency is covered.',
    image: '/brand/service-hns.webp',
    link: '/services#hns',
  },
  {
    icon: Droplets,
    title: 'Water Curtain',
    description:
      'A suspended sheet of falling water that adds movement, depth, and visual drama — an elegant feature for any event environment.',
    image: null,
    link: '/services#watercurtain',
  },
];

export const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#F5F8FA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Header */}
        <div className="text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.3em] text-[#17798A] font-bold mb-4"
          >
            What We Do
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#111111] mb-6"
          >
            Our <span className="text-[#2A93A6]">Expertise</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[#666666] text-base sm:text-lg max-w-xl mx-auto"
          >
            End-to-end event production services tailored to your vision.
          </motion.p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.05 * index }}
            >
              <Link
                to={service.link}
                data-testid={`service-card-${index}`}
                className="service-card group relative h-[400px] overflow-hidden bg-white border border-[#E5E9ED] hover:border-[#47c3d6]/60 block rounded-xl shadow-sm"
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  {service.image ? (
                    <>
                      <img
                        src={service.image}
                        alt={service.title}
                        loading={index < 3 ? 'eager' : 'lazy'}
                        decoding="async"
                        className="service-card-image w-full h-full object-cover transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/60 to-transparent opacity-90 group-hover:opacity-70 transition-opacity duration-500" />
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-[#0D1A1C] border-2 border-dashed border-[#47c3d6]/60">
                      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#47c3d6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>
                      <span className="text-[#47c3d6] text-xs font-semibold tracking-widest uppercase">Photo Coming Soon</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="relative z-10 flex flex-col h-full justify-end p-8">
                  {/* Icon */}
                  <div className="w-12 h-12 bg-[#47c3d6]/10 border border-[#47c3d6]/30 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#47c3d6]/20 transition-colors">
                    <service.icon size={22} className="text-[#47c3d6]" />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#47c3d6] transition-colors">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-[#A3A3A3] text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>

                  {/* Link */}
                  <div className="flex items-center gap-2 text-[#47c3d6] text-sm font-medium opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                    <span>Learn More</span>
                    <ArrowUpRight size={16} />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
